import React, { useMemo, useState } from 'react';

import { IExercise } from '../../../types/interfaces/exercise';
import { useStyles } from './PublicExercises.styles';
import theme from '../../../styles/theme';
import { Box } from '@mui/material';
import Search from '../../../components/reusable/search/Search';
import {
  useDeleteExercise,
  useLazyGetAllExercises,
} from '../../../services/redux/api/exercisesAPI';
import ExerciseList from '../exercise-public-requests/exercise-list/ExerciseList';
import { useExercise } from '../useExercise';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import AddActionButton from '../../../components/designe/ActionButtons/AddActionButton';
import ExercisesListWithPanel from '../exercises-list-with-panel/ExercisesListWithPanel';
import Exercise from '../exercise/Exercise';
import NewExercise from '../new-exercise/NewExercise';
import type { ExercisePanelMode } from '../exercises-list-with-panel/ExercisesListWithPanel';
import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import { ExerciseScope } from '../../../shared/constants';
import { useDebounce } from '../../../hooks/use-debounce';
import { useLazyLoadGeneral } from '../../../hooks/use-lazy-load-general';

type VisibilityFilterValue = 'all' | 'public' | 'private';
type TranslationCompletenessFilterValue = 'all' | 'complete' | 'incomplete';

export default function PublicExercises() {
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 500);
  const [translationCompletenessFilter, setTranslationCompletenessFilter] =
    useState<TranslationCompletenessFilterValue>('all');
  const [visibilityFilter, setVisibilityFilter] =
    useState<VisibilityFilterValue>('all');
  const { t } = useTranslation();

  const translationCompletenessLazyParams = useMemo(() => {
    if (translationCompletenessFilter === 'complete') {
      return {
        hasTitleTranslation: true,
        hasDescriptionTranslation: true,
      };
    }

    if (translationCompletenessFilter === 'incomplete') {
      return {
        hasTitleTranslation: false,
        hasDescriptionTranslation: false,
      };
    }

    return {};
  }, [translationCompletenessFilter]);

  const lazyQueryParams = useMemo(
    () => ({
      isPublic: true,
      ...(visibilityFilter !== 'all'
        ? { scope: visibilityFilter as ExerciseScope }
        : {}),
      ...translationCompletenessLazyParams,
    }),
    [visibilityFilter, translationCompletenessLazyParams],
  );
  const { items: exercises, footerRef } = useLazyLoadGeneral<IExercise>(
    useLazyGetAllExercises,
    debouncedFilter,
    15,
    lazyQueryParams,
  );
  const { classes } = useStyles(theme);
  const { renderExerciseActionButton } = useExercise();
  const [deleteExercise] = useDeleteExercise();
  const [exerciseToDeleteId, setExerciseToDeleteId] = useState<string>(null);

  const [panelState, setPanelState] = useState<{
    exerciseId: string | null;
    mode: ExercisePanelMode;
  }>({ exerciseId: null, mode: 'view' });

  const panelOpen = panelState.exerciseId !== null || panelState.mode === 'new';

  function openPanel(exerciseId: string | null, mode: ExercisePanelMode) {
    setPanelState({ exerciseId, mode });
  }

  function closePanel() {
    setPanelState({ exerciseId: null, mode: 'view' });
  }

  function onEditClick(id: string) {
    openPanel(id, 'edit');
  }

  function onExerciseClick(exercise: IExercise) {
    openPanel(exercise.id, 'view');
  }

  function onAddClick() {
    openPanel(null, 'new');
  }

  function onDelete(id: string) {
    deleteExercise(id)
      .unwrap()
      .then(() => {
        setExerciseToDeleteId(null);
        if (panelState.exerciseId === id) {
          closePanel();
        }
      })
      .catch((e) => console.log(e));
  }

  const listContent = (
    <Box>
      <Box display="flex" gap={1} justifyContent="space-between">
        <Search
          fullWidth
          value={filter}
          onChange={setFilter}
          className={classes.exercisesListSearch}
        />
        <AddActionButton onClick={onAddClick} />
      </Box>
      <Box className={classes.filtersRow}>
        <AppSelectWrapper
          label={t('moderatorDashboard.translationCompleteness')}
          defaultValue={
            [
              {
                id: 'all',
                name: t('moderatorDashboard.translationCompleteness_all'),
              },
              {
                id: 'incomplete',
                name: t(
                  'moderatorDashboard.translationCompleteness_incomplete',
                ),
              },
              {
                id: 'complete',
                name: t('moderatorDashboard.translationCompleteness_complete'),
              },
            ].find((option) => option.id === translationCompletenessFilter) ||
            null
          }
          options={[
            {
              id: 'all',
              name: t('moderatorDashboard.translationCompleteness_all'),
            },
            {
              id: 'incomplete',
              name: t('moderatorDashboard.translationCompleteness_incomplete'),
            },
            {
              id: 'complete',
              name: t('moderatorDashboard.translationCompleteness_complete'),
            },
          ]}
          onChange={(e) =>
            setTranslationCompletenessFilter(e?.target?.value?.id || 'all')
          }
          disableClear
          disableSorting
        />
        <AppSelectWrapper
          label={t('moderatorDashboard.visibility')}
          defaultValue={
            [
              { id: 'all', name: t('common.all') },
              { id: 'private', name: t('exercises.scope_private') },
              { id: 'public', name: t('exercises.scope_public') },
            ].find((option) => option.id === visibilityFilter) || null
          }
          options={[
            { id: 'all', name: t('common.all') },
            { id: 'private', name: t('exercises.scope_private') },
            { id: 'public', name: t('exercises.scope_public') },
          ]}
          onChange={(e) => setVisibilityFilter(e?.target?.value?.id || 'all')}
          disableClear
          disableSorting
        />
      </Box>

      <ExerciseList
        showPublicStatus
        exercises={exercises}
        showSearch={false}
        showTranslationsError={true}
        getActionButtons={(exercise) =>
          renderExerciseActionButton(exercise, onEditClick, (id) =>
            setExerciseToDeleteId(id),
          )
        }
        onExerciseClick={onExerciseClick}
      />
      <Box ref={footerRef} mb={1} />
      <ConfirmDialog
        open={exerciseToDeleteId}
        onClose={() => setExerciseToDeleteId(null)}
        title={t('common.delete')}
        onSubmit={() => {
          onDelete(exerciseToDeleteId);
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </Box>
  );

  const panelContent = panelOpen ? (
    panelState.mode === 'new' ? (
      <NewExercise exerciseId={null} onBack={closePanel} />
    ) : panelState.mode === 'edit' ? (
      <NewExercise exerciseId={panelState.exerciseId} onBack={closePanel} />
    ) : (
      <Exercise
        exerciseId={panelState.exerciseId}
        onBack={closePanel}
        onEditClick={() =>
          panelState.exerciseId && openPanel(panelState.exerciseId, 'edit')
        }
      />
    )
  ) : null;

  return (
    <ExercisesListWithPanel
      listContent={listContent}
      panelContent={panelContent}
      panelOpen={panelOpen}
      onPanelClose={closePanel}
    />
  );
}
